/* eslint-disable max-len */
import { ClassPrice, PricesType, ProductMapPrices } from '../models/product-prices.model';

import { getTimeZoneFromCountry } from '@core/application/use-cases/dates/get-timezone-from.country.use-case';
import { DuttiDate, DuttiDateFormat } from '@frontend/common/duttidate';

export function getTimezoneAdjustedCurrentDate(currentDate: DuttiDate, countryCode: string): DuttiDate {
    const timezone = getTimeZoneFromCountry(countryCode);
    const formattedDate = currentDate.format(DuttiDateFormat.DATE_HOUR_MINUTE_SECOND);
    return new DuttiDate(formattedDate, DuttiDateFormat.DATE_HOUR_MINUTE_SECOND, timezone);
}

export function isAfterVipSales(currentDate: DuttiDate, vipSalesDateRange: any, countryCode: string): boolean {
    const currentDateZ = getTimezoneAdjustedCurrentDate(currentDate, countryCode);
    const vipEndDate = new DuttiDate(
        vipSalesDateRange.to,
        DuttiDateFormat.DATE_HOUR_MINUTE_SECOND,
        getTimeZoneFromCountry(countryCode)
    );
    return currentDateZ.isAfter(vipEndDate);
}

export function handlePriceType(
    mapPrices: ProductMapPrices,
    hasConfigGrid: boolean,
    hasConfigProduct: boolean,
    crmActive: boolean
): { primaryPrice: string; secondaryPrice: string } {
    const { price, oldPrice, originalPrice } = mapPrices;
    let primaryPrice = `${ClassPrice.primary} ${ClassPrice.sizeL}`;
    let secondaryPrice = '';

    if (price.type === PricesType.PRICE && oldPrice.value) {
        if (oldPrice.value < price.value && !originalPrice.value) {
            primaryPrice = ClassPrice.sizeL;
        } else {
            primaryPrice = `${ClassPrice.primarySales} ${ClassPrice.sizeL}`;
        }
    } else if (price.type === PricesType.FUTUREPRICE && oldPrice.value) {
        if (hasConfigGrid || hasConfigProduct) {
            primaryPrice = crmActive ? `${ClassPrice.primaryFeel} ${ClassPrice.sizeL}` : `${ClassPrice.primaryNwl} ${ClassPrice.sizeL}`;
        } else {
            primaryPrice = `${ClassPrice.primarySales} ${ClassPrice.sizeL}`;
        }

        secondaryPrice = `${ClassPrice.primary} ${ClassPrice.sizeL}`;
    }

    return { primaryPrice, secondaryPrice };
}

export function handleVipPricingLogic(
    userIsVip: boolean,
    crmActive: boolean,
    afterVipSales: boolean,
    hasConfigGrid: boolean,
    hasConfigProduct: boolean,
    oldPrice: any
): { primaryPrice: string; secondaryPrice: string } {
    let primaryPrice = `${ClassPrice.primary} ${ClassPrice.sizeL}`;
    let secondaryPrice = '';

    if (oldPrice.value) {
        if (hasConfigGrid || hasConfigProduct) {
            if (crmActive && !afterVipSales) {
                primaryPrice = `${ClassPrice.primaryFeel} ${ClassPrice.sizeL}`;
            } else {
                primaryPrice = `${ClassPrice.primaryNwl} ${ClassPrice.sizeL}`;
            }
        } else {
            primaryPrice = `${ClassPrice.primarySales} ${ClassPrice.sizeL}`;
        }

        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        if (!userIsVip && !afterVipSales) {
            secondaryPrice = `${ClassPrice.primary} ${ClassPrice.sizeL}`;
        }
    }

    if (userIsVip) {
        secondaryPrice = `${ClassPrice.throughPriceBlack} ${ClassPrice.sizeL}`;
    }

    return { primaryPrice, secondaryPrice };
}

export function handleRebajasSinCortePricing(
    userIsVip: boolean,
    crmActive: boolean,
    afterVipSales: boolean,
    hasConfigGrid: boolean,
    hasConfigProduct: boolean,
    oldPrice: any
): { primaryPrice: string; secondaryPrice: string } {
    let primaryPrice = `${ClassPrice.primary} ${ClassPrice.sizeL}`;
    let secondaryPrice = '';

    if (userIsVip && oldPrice.value) {
        if (hasConfigGrid || hasConfigProduct) {
            if (crmActive && !afterVipSales) {
                primaryPrice = `${ClassPrice.primaryFeel} ${ClassPrice.sizeL}`;
            } else {
                primaryPrice = `${ClassPrice.primaryNwl} ${ClassPrice.sizeL}`;
            }
        } else {
            primaryPrice = `${ClassPrice.primarySales} ${ClassPrice.sizeL}`;
        }
        secondaryPrice = `${ClassPrice.throughPriceBlack} ${ClassPrice.sizeL}`;
    } else if (!afterVipSales) {
        secondaryPrice = `${ClassPrice.primary} ${ClassPrice.sizeL}`;
    }

    return { primaryPrice, secondaryPrice };
}

export function handleTriplePriceRemark(
    hasPromotion: boolean,
    triplePriceRemark: boolean,
    oldPrice: any,
    originalPrice: any,
    primaryPrice: string
): string {
    if (triplePriceRemark && !oldPrice.value && !originalPrice.value && hasPromotion) {
        return `${primaryPrice} ${ClassPrice.textPromotion}`;
    }
    return primaryPrice;
}

export function getPriceClassesUseCase(
    mapPrices: ProductMapPrices,
    hasPromotion: boolean,
    crmActive: boolean,
    triplePriceRemark: boolean,
    textsDiscountsPrewarmVea: any,
    page: string,
    userIsVip: boolean,
    countryCode: string,
    vipSalesDateRange: any,
    currentDate: DuttiDate,
    enableRebajasSinCorte: boolean,
    skipCheckPage = false
) {
    const { price, oldPrice, originalPrice } = mapPrices;
    const productPage = skipCheckPage ? false : page === 'ItxProductPage';
    const hasConfigProduct = productPage && textsDiscountsPrewarmVea.product_view;
    const hasConfigGrid = !productPage && textsDiscountsPrewarmVea.grids;

    const afterVipSales = isAfterVipSales(currentDate, vipSalesDateRange, countryCode);

    // Handle primary and secondary price based on price type
    let { primaryPrice, secondaryPrice } = handlePriceType(mapPrices, hasConfigGrid, hasConfigProduct, crmActive);

    // Handle VIP-related pricing in an independent block // TODO ECOMDUTI-26776: Deprecar precios VIP Fase 2 (Eliminar codigo legacy)
    if (price.type === PricesType.VIPPRICE) {
        const vipPrices = handleVipPricingLogic(userIsVip, crmActive, afterVipSales, hasConfigGrid, hasConfigProduct, oldPrice);
        primaryPrice = vipPrices.primaryPrice;
        secondaryPrice = vipPrices.secondaryPrice || secondaryPrice;
    }

    // Handle enableRebajasSinCorte in a separate block
    if (enableRebajasSinCorte) {
        const rebajasPrices = handleRebajasSinCortePricing(userIsVip, crmActive, afterVipSales, hasConfigGrid, hasConfigProduct, oldPrice);
        primaryPrice = rebajasPrices.primaryPrice;
        secondaryPrice = rebajasPrices.secondaryPrice || secondaryPrice;
    }

    // Apply promotions if needed
    primaryPrice = handleTriplePriceRemark(hasPromotion, triplePriceRemark, oldPrice, originalPrice, primaryPrice);

    // Default secondary price if none is set
    if (secondaryPrice === '') {
        secondaryPrice = `${ClassPrice.throughPriceBlack} ${ClassPrice.sizeL}`;
    }

    return {
        primaryPrice,
        secondaryPrice,
        terciaryPrice: `${ClassPrice.sizeL} ${ClassPrice.throughPrice}`,
        lastReducedPrice: `${ClassPrice.sizeL} ${ClassPrice.throughPrice}`,
    };
}
